import { Component, OnInit, OnDestroy, Inject, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject, debounceTime, distinctUntilChanged } from "rxjs";
import { RequestService, LayoutUtilsService, LoaderService, ConfirmEntityDialogComponent } from "src/app/shared";
import { SignalingService } from "src/app/shared/services/signaling.service";
import { CaseService } from "src/app/shared/services/case.service";
import { DocumentDetailedDialogComponent } from "../../documents-dialog/document-detailed-dialog/document-detailed-dialog.component";
import { getResponsiveDialogWidth } from "src/app/shared/helpers";
import { DocumentUploadDialogComponent } from "../../documents-dialog/document-upload-dialog/document-upload-dialog.component";
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
const MY_FORMATS = {
    parse: {
        dateInput: ['YYYY/MM/DD'],
    },
    display: {
        dateInput: 'YYYY/MM/DD',
        monthYearLabel: 'YYYY MMM',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'YYYY MMMM',
    },
};

interface DialogData {
    title: string;
    caseId: string;
    selectedFiles: any[];
    type: string;
    editMode: boolean;
    metadata: any;
    group?: string;
    ai_flag?: string;
    /** When false, uploads documents without creating a case note. Defaults to true. */
    add_note?: boolean;
}


@Component({
    selector: 'app-material-manage-dialog',
    templateUrl: './material-manage-dialog.component.html',
    styleUrls: ['./material-manage-dialog.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
    ]

})
export class MaterialManageDialogComponent implements OnInit, OnDestroy {

    private subscriptions: any = [];
    public selectedUser: any;
    public isAdmin: boolean = false;
    public isSuperAdmin: boolean = false;
    public isInvestigator: boolean = false;
    public selectedFiles: string[] = [];
    public titleDisplayName: string = undefined;

    public selectedType: string = undefined;

    public editMode: boolean = false;
    public isPopUpOpener: boolean = false;
    public caseId: string = undefined;
    public profile: any = undefined;
    public allDocs: any = {};
    public loading: boolean = true;
    public pageSize: number = 10;
    public pageIndexAll: number = 0;
    public pageSizeAll: number = 10;
    public searchValue: string = '';
    public selectedStartdate: any = '';
    public selectedEnddate: any = '';

    private noteMetadata: any = undefined;
    private searchSubject: BehaviorSubject<any> = new BehaviorSubject<any>(undefined);


    @ViewChild('allPaginator') allPaginator!: MatPaginator;
    constructor(public dialogRef: MatDialogRef<MaterialManageDialogComponent>, @Inject(MAT_DIALOG_DATA) public data: DialogData, private requestService: RequestService, private translate: TranslateService, private layoutUtilsService: LayoutUtilsService, private loader: LoaderService, private formBuilder: FormBuilder, private signalingService: SignalingService, private dialog: MatDialog, private caseService: CaseService) {
        this.isPopUpOpener = window.opener ? true : false;
    }

    ngOnInit(): void {
        this.noteMetadata = this.data.metadata;
        this.selectedFiles = this.data.selectedFiles || [];
        this.selectedType = this.data.type || undefined;
        this.editMode = this.data.editMode || false;
        this.caseId = this.data.caseId || '';

        this.subscriptions.push(
            this.requestService.currentUserSubject.subscribe((data) => {
                if (data) {
                    this.selectedUser = data;
                    this.isAdmin = this.requestService.isUserRoleAdmin();
                    this.isSuperAdmin = this.requestService.isUserRoleSuperAdmin();
                    this.isInvestigator = this.requestService.isUserRoleInvestigator();
                }
            })
        );

        this.subscriptions.push(this.searchSubject.pipe(
            debounceTime(150),
            distinctUntilChanged()
        ).subscribe(search => {
            if (search) {
                this.searchValue = search.value;
                this.getDocuments(undefined, this.searchValue, this.selectedType);
            }
        }));
        this.getDocuments();
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    search(event: any, type: string = undefined) {
        this.searchSubject.next({ value: event.value });
    }
    selectDateRange() {
        this.getDocuments(undefined, this.searchValue);
    }

    clearDateRange(event: Event) {
        event.stopPropagation();
        this.selectedStartdate = '';
        this.selectedEnddate = '';
        this.getDocuments(undefined, this.searchValue);
    }
    confirm(): void {
        this.dialogRef.close(this.selectedFiles);
    }
    close(): void {
        this.dialogRef.close();
    }

    getDocuments(event?: PageEvent, searchValue = '', type: string = this.selectedType) {
        this.loading = true;
        this.loader.display(true);
        let pageIndex = event?.pageIndex || 0;
        let pageSize = event?.pageSize || this.pageSize;
        let selectedIds = this.editMode ? this.selectedFiles : undefined;
        this.caseService.getDocuments(pageIndex, pageSize, this.caseId, searchValue, type, selectedIds, this.selectedStartdate, this.selectedEnddate, this.data.group || undefined).then((docs: any) => {
            this.allDocs = docs;
            if (this.allPaginator)
                this.allPaginator.length = docs.pagination.total;
        }).catch((e) => {
            this.layoutUtilsService.showNotification(this.translate.instant('Error: ') + e, this.translate.instant('Dismiss'));
            this.loading = false;
            this.loader.display(false);
        }).finally(() => {
            this.loading = false;
            this.loader.display(false);
        });
    }

    delete(event, docId: string) {
        event.preventDefault();
        event.stopImmediatePropagation();
        event.stopPropagation();
        const dialog = this.dialog.open(ConfirmEntityDialogComponent, {
            autoFocus: false,
            data: {
                description: this.translate.instant(
                    `Are you sure you want to delete this item?`
                ),
                confirmbtn: this.translate.instant('Delete'),
                cancelbtn: this.translate.instant('Cancel')
            },
            width: 'fit-content',
        });
        dialog.afterClosed().subscribe((result) => {
            if (result) {
                this.loader.display(true);
                this.requestService.postRequest('doc', docId + '/delete', {}, (data, error) => {
                    if (data) {
                        this.signalMaterialUpdate('delete', docId);
                        this.layoutUtilsService.showNotification(this.translate.instant('Deleted successfully'), this.translate.instant('Dismiss'));
                    }
                    if (error) {
                        this.layoutUtilsService.showNotification(this.translate.instant('An error has occurred. Please try again later.'), this.translate.instant('Dismiss'));
                    }
                    this.loader.display(false);
                    this.caseService.documentsUpdated.next(true);
                    this.getDocuments();
                });
            }
        });
    }

    private signalMaterialUpdate(action: string, docId?: string) {
        if (!this.caseId) return;
        this.signalingService.publish(`case-${this.caseId}`, {
            type: 'material-updated',
            caseId: this.caseId,
            action: action,
            docId: docId
        }, this.signalingService.materialTopicName);
    }

    add(type: any = undefined) {
        const metadata = this.noteMetadata?.fields
            ? this.noteMetadata
            : (Array.isArray(this.noteMetadata) ? { fields: this.noteMetadata } : { fields: [] });
        const dialogRef = this.dialog.open(DocumentUploadDialogComponent, {
            autoFocus: false,
            width: getResponsiveDialogWidth(),
            data: {
                caseId: this.caseId,
                type: type || this.selectedType || 'Media',
                metadata,
                ai_flag: this.data.ai_flag,
                add_note: this.data.add_note !== false,
                group: this.data.group || undefined
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.caseService.documentsUpdated.next(true);
                this.getDocuments();
            }
        });
    }
    openDialog(event, item: any) {
        if (event) {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
        }

        const dialog = this.dialog.open(DocumentDetailedDialogComponent, {
            autoFocus: false,
            width: 'fit-content',
            minWidth: '30vw',
            disableClose: false,
            data: {
                docId: item._id,
                name: item.name,
                url: item.url,
                type: item.type,
                createdAt: item.createdAt,
                createdName: item.createdName,
            }
        })
    }
    selectItem(item: any) {
        if (this.editMode) {
            this.openDialog(undefined, item);
        } else {
            const index = this.selectedFiles.indexOf(item._id);
            if (index === -1) {
                this.selectedFiles.push(item._id); // Select
            } else {
                this.selectedFiles.splice(index, 1); // Deselect
            }
        }
    }

    toggleSelect(event: Event, item: any) {
        if (event) {
            event.preventDefault();
            event.stopImmediatePropagation();
            event.stopPropagation();
        }
        if (!this.editMode) {
            this.selectItem(item);
        }
    }
}